import { useTheme } from "@emotion/react";
import { useRef, useState } from "react";
import {
  CategoryScale,
  Chart as ChartJS,
  ChartType,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  BarController,
  BarElement,
  PointElement,
  Title,
  Tooltip,
  TooltipItem,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { lineDatasetsType } from "../../adminDashboardPage/optimus/optimusPerformanceGraphing/optimusLineChart";
import {
  BreakdownTypeEnum,
  InsightDistributionByDate,
} from "../../../graphqlExternal/generated";
import { returnRoundedNumber, unsafeRateName } from "..";

type BarLineGraphProps = {
  campaignData: InsightDistributionByDate[];
  error: boolean;
  breakDownType?: BreakdownTypeEnum;
};

export enum kpiEnum {
  IMPRESSIONS = "allImpressions",
  BRAND_UNSAFE_RATE = "brandUnsafeRate",
}

export const BarLineGraph = ({
  campaignData,
  error = false,
  breakDownType = BreakdownTypeEnum.Combined,
}: BarLineGraphProps) => {
  const chakraTheme = useTheme();
  const xAxis: Date[] = [];
  const [primaryKpi] = useState({
    label: "Impressions",
    value: kpiEnum.IMPRESSIONS,
  });
  const [secondaryKpi] = useState({
    label: "Unsafe Rate",
    value: kpiEnum.BRAND_UNSAFE_RATE,
  });

  const chartRef = useRef<ChartJS | null>(null);
  let campaignDataDisplay: any[] = [];

  ChartJS.register(
    ChartDataLabels,
    CategoryScale,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    BarController,
    BarElement,
    Title,
    Tooltip,
    Legend
  );

  const distinctColors: string[] = Object.values(
    chakraTheme.colors.distinctDataColors
  );

  const illumaColours: string[] = Object.values(chakraTheme.colors.primary);

  if (error || !campaignData) {
    return <>{"No Data Available for selected filters"}</>;
  }

  // function to add relevant data to x and yaxis lists of data to be plotted
  const prepareDatasets = () => {
    let datasets: lineDatasetsType[] = [];

    if (campaignData && campaignData.length > 0) {
      campaignDataDisplay = [...campaignData];
      campaignDataDisplay.sort((a, b) => {
        const dateA = new Date(a.aggregationDate).getTime();
        const dateB = new Date(b.aggregationDate).getTime();

        return dateA - dateB;
      });
    }

    xAxis.push(...campaignDataDisplay.map((data: any) => data.aggregationDate));
    const primaryKpiList = campaignDataDisplay.map(
      (data: any) => data[primaryKpi.value] || 0
    );
    const secondaryKpiList = campaignDataDisplay.map(
      (data: any) => returnRoundedNumber(data[secondaryKpi.value]) || 0
    );

    datasets = [
      {
        type: "bar" as ChartType,
        label: formatAxis(primaryKpi.value, breakDownType),
        data: primaryKpiList,
        borderColor: illumaColours[2],
        backgroundColor: illumaColours[2],
        borderWidth: 2,
        yAxisID: "y",
        order: 1,
      },
      {
        type: "line" as ChartType,
        label: formatAxis(secondaryKpi.value, breakDownType),
        data: secondaryKpiList,
        borderColor: distinctColors[18],
        backgroundColor: distinctColors[18],
        borderWidth: 2,
        yAxisID: "y2",
        order: 0,
      },
    ];
    return { datasets: datasets };
  };

  const options = {
    responsive: true,
    plugins: {
      datalabels: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<"bar" | "line">) {
            const label = context.dataset.label || "";
            const value =
              context.parsed.y !== undefined
                ? context.parsed.y
                : context.parsed;

            if (Number.isInteger(value)) {
              return `${label}: ${value.toLocaleString()}`;
            }
            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: "Date",
        },
      },
      y: {
        stacked: false,
        display: true,
        position: "left",
        title: {
          display: true,
          text: formatAxis(primaryKpi.value, breakDownType),
        },
        grace: "25%",
      },
      y2: {
        stacked: false,
        display: true,
        position: "right",
        title: {
          display: true,
          text: formatAxis(secondaryKpi.value, breakDownType),
        },
        grace: "25%",
        grid: {
          drawOnChartArea: false, // only want the grid lines for one axis to show up
        },
      },
    },
    layout: {
      padding: {
        top: 20,
      },
    },
  };

  const chartValues = prepareDatasets();

  const chartData = {
    labels: xAxis,
    datasets: chartValues["datasets"],
  };

  return (
    <>
      <Chart ref={chartRef} type={"bar"} options={options} data={chartData} />
    </>
  );
};

const formatAxis = (
  kpiName: string,
  breakDownType: BreakdownTypeEnum = BreakdownTypeEnum.Combined
) => {
  switch (kpiName) {
    case kpiEnum.IMPRESSIONS:
      return "Impressions";
    case kpiEnum.BRAND_UNSAFE_RATE:
      return `${unsafeRateName(breakDownType)} Unsafe Rate (%)`;
    default:
      return "Impressions";
  }
};
